import {
    ApplicationConfig,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
    inject
} from '@angular/core'
import { Router, isActive, provideRouter, withRouterConfig, withViewTransitions } from '@angular/router'
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http'
import { provideTranslateService, TranslateService } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'

import { routes } from './app.routes'
import { ApiInterceptorsService } from './interceptors/api.interceptor'
import { CacheInterceptor } from './interceptors/cache.interceptor'
import { HttpErrorsInterceptorService } from './interceptors/http-errors.interceptor'
import { ConstantsService } from './services/constants.service'
import { OverridesService } from './services/overrides.service'
import { FcmService } from './services/fcm.service'
import { UIHelpers } from './helpers/ui-helpers'
import { RgHttpService } from './core/http/rg-http.service'
import { provideKeyboardShortcuts } from './core/keyboard/global-shortcuts'

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            routes,
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            // Page-change animation; styles in assets/styles/motion.scss (::view-transition-*).
            withViewTransitions({
                skipInitialTransition: true,
                onViewTransitionCreated: ({ transition }) => {
                    const router = inject(Router)
                    const target = router.currentNavigation()?.finalUrl
                    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                    // Only animate real page changes: skip when just query params/fragment change
                    // (grid filters, tabs) or when the same URL is reloaded.
                    const samePage = !!target && isActive(target, router, {
                        paths: 'exact', matrixParams: 'exact', queryParams: 'ignored', fragment: 'ignored'
                    })()
                    if (reducedMotion || samePage) {
                        transition.skipTransition()
                    }
                }
            })
        ),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptorsService,
            multi: true
        },
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorsInterceptorService,
            multi: true
        },
        provideTranslateService({
            lang: localStorage.getItem('lang') || 'en',
            fallbackLang: 'en',
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json'
            })
        }),
        provideAppInitializer(() => {
            const translate = inject(TranslateService)
            const defaultLang = localStorage.getItem('lang') || 'en'

            return new Promise<void>((resolve) => {
                translate.use(defaultLang).subscribe({
                    next: () => resolve(),
                    error: (error: unknown) => {
                        console.error(
                            `Failed to load language ${defaultLang}:`,
                            error
                        )
                        resolve() // Resolve anyway to prevent app from hanging
                    }
                })
            })
        }),
        // localStorage.user outlives the server session. Probe the session once at
        // startup (not awaited, so it never delays boot): a dead session returns 401,
        // and HttpErrorsInterceptorService then logs the user out and shows /login.
        provideAppInitializer(() => {
            const http = inject(RgHttpService)
            try {
                const stored = localStorage.getItem('user')
                const type = stored ? JSON.parse(stored)?.userable_type : null
                if (type === 'customer' || type === 'workshop' || type === 'system') {
                    // system (admin panel) users are served under the admin prefix
                    const prefix = type === 'system' ? 'admin' : type
                    http.getResource(`${prefix}/auth/user-data`).subscribe({
                        error: () => { /* 401 is handled by the interceptor */ }
                    })
                }
            } catch {
                // corrupt localStorage value: ignore, normal flow will re-login
            }
        }),
        provideAppInitializer(() => {
            const overrides = inject(OverridesService)
            return overrides.init()
        }),
        provideKeyboardShortcuts(),
        ConstantsService,
        OverridesService,
        FcmService,
        UIHelpers
    ]
}
